import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import { isEqual } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';
import tw from 'twin.macro';

import BookingIcon from '../../assets/svg/BookingIcon';
import CalendarIcon from '../../assets/svg/CalendarIcon';
import ConfirmIcon from '../../assets/svg/ConfirmIcon';
import MailIcon from '../../assets/svg/MailIcon';
import RefreshIcon from '../../assets/svg/RefreshIcon';
import {
  ChartAppointmentOutcomes,
  ChartDialActivities,
  ChartDialOutcomes,
  ChartNumber,
  TeamFilter,
} from '../../components/Dashboard';
import { SendReportModal } from '../../components/Modals';
import { Button, ButtonExport, DateFilter } from '../../components/Shared';
import {
  DATE_TIME_FORMAT,
  REPORT_FILE_FORMAT,
  USER_ROLE,
} from '../../constants';
import { saveCSVReport, saveXLSXReport } from '../../helpers';
import { useAuthContext } from '../../hooks';
import { Layout } from '../../layouts';

const StyledCircularProgress = styled(CircularProgress)`
  ${tw`text-brand-300`}
`;

const STATE = {
  HAS_DATA: 'hasData',
  IS_DATA_LOADING: 'isDataLoading',
  IS_DATA_ERROR: 'isDataError',
};

const getState = (isDataLoading, isDataError) => {
  if (isDataLoading) return STATE.IS_DATA_LOADING;
  if (isDataError) return STATE.IS_DATA_ERROR;
  return STATE.HAS_DATA;
};

export const Dashboard = () => {
  const { userRole } = useAuthContext();

  const isManager = userRole === USER_ROLE.MANAGER;

  const [isSendReportModalOpened, setIsSendReportModalOpened] = useState(false);

  const openSendReportModal = () => setIsSendReportModalOpened(true);
  const closeSendReportModal = () => setIsSendReportModalOpened(false);

  const initialFilters = {
    ...(isManager && { callerId: null }),
    startDate: moment(new Date()).format(DATE_TIME_FORMAT.YYYY_MM_DD_DASH),
    endDate: moment(new Date()).format(DATE_TIME_FORMAT.YYYY_MM_DD_DASH),
  };

  const [filters, setFilters] = useState(initialFilters);

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  const [managerTeam, setManagerTeam] = useState([]);
  const [isManagerTeamLoading, setIsManagerTeamLoading] = useState(true);
  const [isManagerTeamError, setIsManagerTeamError] = useState(false);

  const fetchManagerTeam = useCallback(async () => {
    setIsManagerTeamLoading(true);
    setIsManagerTeamError(false);

    try {
      const response = await fetch('/get-manager-team');
      if (response.status !== 200) throw new Error();
      const data = await response.json();
      setIsManagerTeamLoading(false);
      setManagerTeam(data.team);
    } catch {
      setIsManagerTeamLoading(false);
      setIsManagerTeamError(true);
      toast.error('Failed to load manager team!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  }, []);

  useEffect(() => {
    if (!isManager) return;
    fetchManagerTeam();
  }, [isManager, fetchManagerTeam]);

  const abortControllerRef = useRef(null);
  const [dashboardData, setDashboardData] = useState({});
  const [isDashboardDataLoading, setIsDashboardDataLoading] = useState(true);
  const [isDashboardDataError, setIsDashboardDataError] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    setIsDashboardDataLoading(true);
    setIsDashboardDataError(false);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch('/dashboard-data', {
        signal: abortControllerRef.current.signal,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(filters),
      });
      if (response.status !== 200) throw new Error();
      const data = await response.json();
      const dashboardData = data.data;
      setIsDashboardDataLoading(false);
      setDashboardData(dashboardData);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setIsDashboardDataLoading(false);
      setIsDashboardDataError(true);
    }
  }, [filters]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const [isExportReportsSubmitting, setIsExportReportsSubmitting] =
    useState(false);

  const exportSelectedReports = async reportFormat => {
    setIsExportReportsSubmitting(true);

    try {
      const response = await fetch('/export-dashboard-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reportFormat,
          startDate: filters.startDate,
          endDate: filters.endDate,
          ...(!!filters.callerId && { callerId: filters.callerId }),
        }),
      });

      if (response.status !== 200) throw new Error();

      if (reportFormat === REPORT_FILE_FORMAT.XLSX) {
        const workBook = await response.json();
        saveXLSXReport(workBook, 'Dashboard Report');
      }

      if (reportFormat === REPORT_FILE_FORMAT.CSV) {
        const blob = await response.blob();
        saveCSVReport(blob, 'Dashboard Report');
      }

      setIsExportReportsSubmitting(false);
    } catch {
      setIsExportReportsSubmitting(false);
      toast.error('Something went wrong, please try again!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const isFiltersChanged = !isEqual(initialFilters, filters);
  const areReportActionsDisabled = isDashboardDataLoading;
  const isButtonExportDisabled =
    areReportActionsDisabled || isExportReportsSubmitting;
  const isButtonSendReportDisabled = areReportActionsDisabled;
  const state = getState(isDashboardDataLoading, isDashboardDataError);

  return (
    <Layout>
      <div className="flex flex-col gap-4 h-full overflow-hidden">
        <div className="flex justify-between gap-3 overflow-auto shrink-0">
          <div className="flex gap-3 shrink-0">
            {isManager && !isManagerTeamError && (
              <TeamFilter
                callerId={filters.callerId}
                onChangeCallerId={setFilters}
                managerTeam={managerTeam}
                isManagerTeamLoading={isManagerTeamLoading}
              />
            )}
            <DateFilter
              startDate={filters.startDate}
              endDate={filters.endDate}
              onChangeDateRange={setFilters}
            />
            {isFiltersChanged && (
              <Button
                title="Clear All"
                variant="text"
                onClick={resetFilters}
                className="!bg-transparent hover:!bg-transparent focus:!bg-transparent text-brand-900 p-2"
              />
            )}
          </div>
          {isManager && (
            <div className="flex gap-3 shrink-0">
              <ButtonExport
                disabled={isButtonExportDisabled}
                onClick={exportSelectedReports}
              />
              <Button
                title="Send Report"
                variant="blue"
                iconStart={
                  <MailIcon
                    className={twMerge(
                      'w-5 h-5 text-brand-900',
                      isButtonSendReportDisabled && 'text-base-400',
                    )}
                  />
                }
                disabled={isButtonSendReportDisabled}
                onClick={openSendReportModal}
              />
            </div>
          )}
        </div>
        {state === STATE.IS_DATA_LOADING && (
          <div className="h-full flex justify-center items-center">
            <StyledCircularProgress size={48} />
          </div>
        )}
        {state === STATE.IS_DATA_ERROR && (
          <div className="h-full flex justify-center items-center flex-col gap-3 bg-brand-300/[0.08] rounded-[20px]">
            <div className="flex justify-center items-center flex-col gap-1.5">
              <span className="text-base-1000 text-[22px]/[28px] font-semibold">
                Failed to load data
              </span>
              <span className="text-base-800 text-[16px]/[24px]">
                Error occurred while loading. Try again.
              </span>
            </div>
            <Button
              title="Reload"
              variant="text"
              className="text-destructive-500 !bg-transparent"
              iconStart={
                <RefreshIcon className="w-5 h-5 text-destructive-500" />
              }
              onClick={fetchDashboardData}
            />
          </div>
        )}
        {state === STATE.HAS_DATA && (
          <div className="grid gap-5 grid-cols-[repeat(auto-fit,minmax(590px,1fr))] overflow-auto">
            <div className="flex flex-col gap-5">
              <ChartDialActivities data={dashboardData.dialActivities} />
              <ChartDialOutcomes data={dashboardData.dialOutcomes} />
            </div>
            <div className="flex flex-col gap-5">
              <div className="grid gap-5 grid-cols-3">
                <ChartNumber
                  title="Performed"
                  Icon={ConfirmIcon}
                  data={dashboardData.appointmentWidgets.performed}
                />
                <ChartNumber
                  title="Rescheduled"
                  Icon={BookingIcon}
                  data={dashboardData.appointmentWidgets.rescheduled}
                />
                <ChartNumber
                  title="Upcoming"
                  Icon={CalendarIcon}
                  data={dashboardData.appointmentWidgets.upcoming}
                />
              </div>
              <ChartAppointmentOutcomes
                data={dashboardData.appointmentOutcomes}
              />
            </div>
          </div>
        )}
        {isSendReportModalOpened && (
          <SendReportModal
            open={isSendReportModalOpened}
            requestURL="/send-dashboard-report"
            requestBody={{
              startDate: filters.startDate,
              endDate: filters.endDate,
              ...(!!filters.callerId && { callerId: filters.callerId }),
            }}
            onClose={closeSendReportModal}
          />
        )}
      </div>
    </Layout>
  );
};
